import { supabase } from '../utils/supabase';
import { escapePostgresLike } from '../utils/sqlSecurity';

/**
 * Help Article Service
 * Manages CRUD operations for help articles in the database
 */
export class HelpArticleService {
  /**
   * Get all articles with optional filters
   * @param {Object} filters - Filter options
   * @param {string} filters.category - Filter by category
   * @param {boolean} filters.is_published - Filter by published status
   * @param {string} filters.search - Search in title and description
   * @param {string[]} filters.helpful_for - Filter by target audience
   * @param {string} filters.urgency - Filter by urgency level
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  static async getAllArticles(filters = {}) {
    try {
      let query = supabase
        .from('help_articles')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.category && filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }

      if (filters.is_published !== undefined) {
        query = query.eq('is_published', filters.is_published);
      }

      if (filters.urgency) {
        query = query.eq('urgency', filters.urgency);
      }

      if (filters.helpful_for && filters.helpful_for.length > 0) {
        query = query.contains('helpful_for', filters.helpful_for);
      }

      if (filters.search && filters.search.trim()) {
        const escapedSearch = escapePostgresLike(filters.search.trim());
        const rawSearch = filters.search.trim();
        query = query.or(
          `title.ilike.%${escapedSearch}%,description.ilike.%${escapedSearch}%,tags.cs.{${rawSearch}}`
        );
      }
      const { data, error } = await query;

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[HelpArticleService] getAllArticles error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get a single article by ID
   * @param {string} id - Article ID
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async getArticleById(id) {
    try {
      const { data, error } = await supabase
        .from('help_articles')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[HelpArticleService] getArticleById error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get a single article by slug
   * @param {string} slug - Article slug
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async getArticleBySlug(slug) {
    try {
      const { data, error } = await supabase
        .from('help_articles')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[HelpArticleService] getArticleBySlug error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create a new article
   * @param {Object} articleData - Article data
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async createArticle(articleData) {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;

      // Generate slug from title if not provided
      const slug = articleData.slug || this.generateSlug(articleData.title);

      const { data, error } = await supabase
        .from('help_articles')
        .insert({
          ...articleData,
          slug,
          created_by: user.id
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[HelpArticleService] createArticle error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update an existing article
   * @param {string} id - Article ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async updateArticle(id, updates) {
    try {
      const { data, error } = await supabase
        .from('help_articles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[HelpArticleService] updateArticle error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete an article
   * @param {string} id - Article ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async deleteArticle(id) {
    try {
      const { error } = await supabase
        .from('help_articles')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('[HelpArticleService] deleteArticle error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Publish or unpublish an article
   * @param {string} id - Article ID
   * @param {boolean} isPublished - Published status
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async togglePublishStatus(id, isPublished) {
    try {
      const { data, error } = await supabase
        .from('help_articles')
        .update({
          is_published: isPublished,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[HelpArticleService] togglePublishStatus error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Increment view count for an article
   * @param {string} id - Article ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async incrementViewCount(id) {
    try {
      const { error } = await supabase.rpc('increment_help_article_views', {
        article_id: id
      });

      // If RPC function doesn't exist, use a direct update
      if (error && (error.code === '42883' || error.code === 'PGRST202')) {
        const { error: updateError } = await supabase
          .from('help_articles')
          .update({
            view_count: supabase.raw('view_count + 1')
          })
          .eq('id', id);

        if (updateError) throw updateError;
      } else if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('[HelpArticleService] incrementViewCount error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Vote on article helpfulness
   * @param {string} articleId - Article ID
   * @param {boolean} isHelpful - Whether the article was helpful
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async voteOnArticle(articleId, isHelpful) {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;

      // Upsert vote (insert or update if exists)
      const { data, error } = await supabase
        .from('help_article_votes')
        .upsert({
          article_id: articleId,
          user_id: user.id,
          is_helpful: isHelpful
        }, {
          onConflict: 'article_id,user_id'
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[HelpArticleService] voteOnArticle error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's vote for an article
   * @param {string} articleId - Article ID
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async getUserVote(articleId) {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;

      const { data, error } = await supabase
        .from('help_article_votes')
        .select('*')
        .eq('article_id', articleId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('[HelpArticleService] getUserVote error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Upload article thumbnail to Supabase Storage
   * @param {File} file - Image file
   * @param {string} articleId - Article ID (for organizing files)
   * @returns {Promise<{success: boolean, url?: string, error?: string}>}
   */
  static async uploadThumbnail(file, articleId) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${articleId}_${Date.now()}.${fileExt}`;
      const filePath = `thumbnails/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('help-article-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('help-article-images')
        .getPublicUrl(filePath);

      return { success: true, url: urlData.publicUrl };
    } catch (error) {
      console.error('[HelpArticleService] uploadThumbnail error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete article thumbnail from Supabase Storage
   * @param {string} thumbnailUrl - URL of the thumbnail to delete
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async deleteThumbnail(thumbnailUrl) {
    try {
      if (!thumbnailUrl) return { success: true };

      // Extract file path from URL
      const urlParts = thumbnailUrl.split('/help-article-images/');
      if (urlParts.length < 2) {
        throw new Error('Invalid thumbnail URL');
      }
      const filePath = urlParts[1];

      const { error } = await supabase.storage
        .from('help-article-images')
        .remove([filePath]);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('[HelpArticleService] deleteThumbnail error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate URL-friendly slug from title
   * @param {string} title - Article title
   * @returns {string} URL-friendly slug
   */
  static generateSlug(title) {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .substring(0, 100); // Limit length
  }

  /**
   * Get article statistics
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async getArticleStats() {
    try {
      const { data, error } = await supabase
        .from('help_articles')
        .select('category, is_published, view_count, helpful_count');

      if (error) throw error;

      const stats = {
        total: data.length,
        published: data.filter(a => a.is_published).length,
        draft: data.filter(a => !a.is_published).length,
        totalViews: data.reduce((sum, a) => sum + (a.view_count || 0), 0),
        totalHelpfulVotes: data.reduce((sum, a) => sum + (a.helpful_count || 0), 0),
        byCategory: {}
      };

      // Group by category
      data.forEach(article => {
        if (!stats.byCategory[article.category]) {
          stats.byCategory[article.category] = 0;
        }
        stats.byCategory[article.category]++;
      });

      return { success: true, data: stats };
    } catch (error) {
      console.error('[HelpArticleService] getArticleStats error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Bulk publish/unpublish articles
   * @param {string[]} ids - Array of article IDs
   * @param {boolean} isPublished - Published status
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async bulkUpdatePublishStatus(ids, isPublished) {
    try {
      const { error } = await supabase
        .from('help_articles')
        .update({
          is_published: isPublished,
          updated_at: new Date().toISOString()
        })
        .in('id', ids);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('[HelpArticleService] bulkUpdatePublishStatus error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Bulk delete articles
   * @param {string[]} ids - Array of article IDs
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async bulkDeleteArticles(ids) {
    try {
      const { error } = await supabase
        .from('help_articles')
        .delete()
        .in('id', ids);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('[HelpArticleService] bulkDeleteArticles error:', error);
      return { success: false, error: error.message };
    }
  }
}

export default HelpArticleService;
